// src/pages/attendance/components/LocationIssues.jsx
//
// Explains why check-in is locked. Every issue says what happened, who can fix
// it (the employee, HR, IT support, or just the connection) and, for device
// settings, the exact steps. Colour follows the owner, so "your side" and
// "company side" problems read differently at a glance.
import React from "react";
import { MapPinOff, LocateOff, WifiOff, Timer, UserX, ShieldAlert, Globe, RefreshCw } from "lucide-react";

const ISSUES = {
  NO_WORK_LOCATION:      { icon: MapPinOff,   owner: "hr" },
  EMPLOYEE_NOT_FOUND:    { icon: UserX,       owner: "hr" },
  MISSING_EMPLOYEE_CODE: { icon: UserX,       owner: "hr" },
  AREA_LOAD_FAILED:      { icon: WifiOff,     owner: "connection" },
  PERMISSION_DENIED:     { icon: ShieldAlert, owner: "you" },
  POSITION_UNAVAILABLE:  { icon: LocateOff,   owner: "you" },
  TIMEOUT:               { icon: Timer,       owner: "you" },
  UNSUPPORTED:           { icon: Globe,       owner: "you" },
  INSECURE_CONTEXT:      { icon: ShieldAlert, owner: "support" },
};

// Retrying can't help until something outside this page changes.
const NO_RETRY = new Set(["UNSUPPORTED", "INSECURE_CONTEXT"]);

const FONT = "'Cairo', sans-serif";

const KEYFRAMES = `
@keyframes locIssueIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes locIssueRing { 0% { box-shadow: 0 0 0 0 var(--ring); } 70%, 100% { box-shadow: 0 0 0 10px transparent; } }
@media (prefers-reduced-motion: reduce) { .loc-issue, .loc-issue-icon { animation: none !important; } }
`;

const LocationIssues = ({ issues, onRetry, currentTheme, isDarkMode, t }) => {
  if (!issues.length) return null;
  const c = currentTheme.colors;
  const toneOf = { you: c.warning, hr: c.primary, support: c.primary, connection: c.danger };
  const canRetry = issues.some((i) => !NO_RETRY.has(i.code));

  return (
    <div role="alert" style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 780, margin: "20px auto 0", textAlign: "start" }}>
      <style>{KEYFRAMES}</style>

      {issues.map((issue, index) => {
        const meta = ISSUES[issue.code] || ISSUES.POSITION_UNAVAILABLE;
        const copy = t.locationIssues[issue.code] || t.locationIssues.POSITION_UNAVAILABLE;
        const tone = toneOf[meta.owner];
        const Icon = meta.icon;

        return (
          <div
            key={issue.code}
            className="loc-issue"
            style={{
              position: "relative", overflow: "hidden",
              display: "flex", gap: 14, padding: "18px 18px 16px",
              borderRadius: 18,
              background: isDarkMode
                ? `linear-gradient(135deg, ${tone}1f, ${tone}08)`
                : `linear-gradient(135deg, ${tone}14, #ffffff 70%)`,
              border: `1px solid ${tone}${isDarkMode ? "40" : "33"}`,
              boxShadow: isDarkMode ? "none" : `0 6px 20px ${tone}14`,
              animation: `locIssueIn .35s ease-out ${index * 80}ms both`,
            }}
          >
            {/* accent bar on the leading edge */}
            <span aria-hidden style={{ position: "absolute", insetInlineStart: 0, top: 14, bottom: 14, width: 3, borderRadius: 3, background: tone }} />

            <div
              className="loc-issue-icon"
              style={{
                "--ring": `${tone}55`,
                flexShrink: 0, width: 44, height: 44, borderRadius: 14,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: `${tone}${isDarkMode ? "2e" : "1f"}`,
                animation: "locIssueRing 2.4s ease-out infinite",
              }}
            >
              <Icon size={22} color={tone} strokeWidth={2.2} />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px 10px", marginBottom: 4 }}>
                <span style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: c.textPrimary }}>{copy.title}</span>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "2px 10px", borderRadius: 999,
                  background: `${tone}1a`, color: tone,
                  fontFamily: FONT, fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap",
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: tone }} />
                  {t.locationIssueOwners[meta.owner]}
                </span>
              </div>

              <p style={{ margin: 0, fontFamily: FONT, fontSize: 13.5, lineHeight: 1.75, color: c.textSecondary }}>{copy.hint}</p>

              {copy.steps?.length > 0 && (
                <ol style={{ listStyle: "none", margin: "12px 0 0", padding: 0, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {copy.steps.map((step, i) => (
                    <li key={step} style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      paddingBlock: 5, paddingInlineStart: 5, paddingInlineEnd: 12,
                      borderRadius: 999,
                      background: isDarkMode ? "rgba(255,255,255,0.05)" : "#fff",
                      border: `1px solid ${c.border}`,
                      fontFamily: FONT, fontSize: 12.5, fontWeight: 600, color: c.textPrimary,
                      whiteSpace: "nowrap",
                    }}>
                      <span style={{
                        width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        background: tone, color: "#fff", fontSize: 11, fontWeight: 800,
                      }}>{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        );
      })}

      {canRetry && (
        <button
          type="button"
          onClick={onRetry}
          style={{
            alignSelf: "center", marginTop: 4,
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "9px 20px", borderRadius: 999,
            background: "transparent", color: c.primary,
            border: `1.5px solid ${c.primary}55`,
            fontFamily: FONT, fontSize: 13.5, fontWeight: 700, cursor: "pointer",
          }}
        >
          <RefreshCw size={15} strokeWidth={2.5} />
          {t.timeTracker.tryAgain}
        </button>
      )}
    </div>
  );
};

export default LocationIssues;
